import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { selectActiveCompany } from "../../../../store/activeCompanySlice";
import { saveLastRoute, showLoginModal } from "../../../../store/auth/authSlice";
import {
  useCancelSubscriptionMutation,
  useCreateVerifiedCustomerPortalSessionMutation,
} from "../../../../store/Billing/billingApiSlice";
import { Modal } from "../../../common/Modal";
import { CommonToast } from "../../../common/Toast";
import { CancelSubscriptionConfirmation } from "../../../common/CancelSubscriptionConfirmatiion";
import { CancelationSuccess } from "../../../common/CancelationSuccess";

import { BillingHeader } from "./BillingHeader";
import { PlanSummaryCard } from "./PlanSummaryCard";
import { BillingHistoryCard } from "./BillingHistoryCard";
import { BillingEmptyState } from "./BillingEmptyState";
import { useBillingData } from "./useBillingData";
import { planPresentation } from "./planPresentation";
import { invoiceHref } from "./billingFormat";

/* u1 Billing. Visual uplift only. Every decision this screen makes is transcribed from components/admin/Dashboard/billing/Subscription.jsx — see... */

/* What the masked-file endpoint says when it refuses. */
export const downloadFailureMessage = (status, body) => {
  const said = body?.message;
  if (status === 404) {
    return said
      ? `${said} The invoice is not in storage for this environment.`
      : "That invoice is not in storage.";
  }
  if (status === 403) return said || "You do not have permission to open that invoice.";
  if (status === 400) return said || "That download link is no longer valid. Reload and try again.";
  return said || `Couldn't download that invoice (${status}).`;
};

/* Transcribed download. The protocol allowlist is the live screen's, kept verbatim: an invoice URL that is not http(s) on an http page — or not https on an... */
export const downloadInvoice = async (url, { onError, onUnauthorized } = {}) => {
  try {
    const parsedUrl = new URL(url, window.location.origin);

    const allowedProtocols =
      window.location.protocol === "https:" ? ["https:"] : ["http:", "https:"];

    if (!allowedProtocols.includes(parsedUrl.protocol)) {
      throw new Error("Invalid URL protocol.");
    }

    const response = await fetch(parsedUrl.toString(), {
      method: "GET",
      credentials: "include",
      cache: "no-store",
    });

    /* A raw fetch does not go through baseQueryWithReauth, so a dead session
       has to be recovered here — the same gap as the profile PDF export. */
    if (response.status === 401) {
      if (onUnauthorized) onUnauthorized();
      return;
    }

    if (!response.ok) {
      /* The endpoint explains itself in the body; read it before giving up. */
      let body = null;
      try {
        body = await response.clone().json();
      } catch {
        /* Not every failure answers with JSON — a proxy or gateway error will
           not. The status alone still gives a useful message. */
      }
      /* The 404 body also carries the underlying storage error. Too raw for a
         toast, but it is the one line that identifies the cause, so it goes to
         the console. */
      if (body?.error) console.error("Invoice download refused:", body.error);
      throw new Error(downloadFailureMessage(response.status, body));
    }

    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = "invoice.pdf";
    link.rel = "noopener noreferrer";
    link.click();

    /* Revoked once the browser has started the download. */
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 100);
  } catch (error) {
    /* The live screen only console.errors here, so a failed download looks like nothing happening. */
    console.error("Error downloading invoice:", error);
    if (onError) onError(error);
  }
};

export const BillingPanel = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeCompany = useSelector(selectActiveCompany);
  const {
    token,
    rows,
    allRows,
    subscriptionData,
    activeSub,
    hidePlan,
    cancelled,
    setCancelled,
  } = useBillingData();

  const [getConfirm, setGetConfirm] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  const [cancelSubscription] = useCancelSubscriptionMutation();
  const [createPortalSession, { isLoading: portalLoading }] =
    useCreateVerifiedCustomerPortalSessionMutation();

  /* Transcribed verbatim, scroll reset included: "Buy a plan" and "Modify
     plan" buy MEMBERSHIP for the company in the switcher, falling back to the
     membership page when nothing is selected. */
  const handleNavigate = () => {
    window.scrollTo(0, 0);
    const companyID = activeCompany?.companyID;
    navigate(companyID ? `/membership/${companyID}` : "/dashboard/membership");
  };

  /* Transcribed: verified subscriptions manage card, plan and cancellation
     through the Stripe Customer Portal, opened in a new tab. */
  const handleManageSubscription = async () => {
    const res = await createPortalSession(token);
    if (res?.data?.portal_url) {
      window.open(res.data.portal_url, "_blank", "noopener,noreferrer");
    } else {
      /* Live code does nothing at all when the portal session comes back
         without a URL, which reads as a dead button. Said out loud instead. */
      CommonToast.error("Couldn't open the billing portal. Please try again.");
    }
  };

  const handleDownload = (url) =>
    downloadInvoice(url, {
      /* The endpoint's own message, so a missing file reads as a missing file
         rather than a broken button. */
      onError: (error) =>
        CommonToast.error(error?.message || "Couldn't download that invoice."),
      onUnauthorized: () => {
        dispatch(saveLastRoute(window.location.pathname));
        dispatch(showLoginModal());
      },
    });

  /* The live "download all" maps over a variable that is never assigned, so it
     downloads nothing — see FINDINGS F-019. Wired here to the payments the
     table actually lists, which is plainly what it was meant to do. */
  const handleDownloadAll = () => {
    rows.map(invoiceHref).filter(Boolean).forEach((url) => handleDownload(url));
  };

  const handleCancelSubscription = async () => {
    const res = await cancelSubscription(activeSub?.stripe_subscription_id);
    if (res) {
      setGetConfirm(false);
      setCancelSuccess(true);
      setCancelled(res?.data?.message);
    }
  };

  const plan = planPresentation(activeSub, { portalLoading, hidePlan });

  /* Transcribed: the empty state shows only when there is nothing on either
     list — not merely when the payable rows are empty. */
  const isEmpty = !(allRows?.length > 0 || subscriptionData?.length > 0);

  /* Transcribed disable rule, all three strings included. */
  const cancelBlocked =
    cancelled === "Cancel Subscription Successfully" ||
    cancelled === "cancelled" ||
    cancelled === "No Subscription";

  /* Transcribed exactly: "No Subscription" disables the control but does NOT
     relabel it — only the two cancelled strings do. */
  const cancelLabel =
    cancelled === "Cancel Subscription Successfully" || cancelled === "cancelled"
      ? "Subscription is Cancelled"
      : "Cancel Subscription";

  /* Transcribed: no cancel control for verified subs (they cancel through the
     Stripe portal) nor for pay-as-you-go, and the live button renders an empty
     label — i.e. nothing to click — when there is no active sub at all. */
  const showCancel =
    activeSub?.subscription_type !== "verified" &&
    Boolean(activeSub) &&
    activeSub?.subscription_type !== "pay-as-you-go";

  const endsOn = (label) =>
    `${label} <b>${new Date(activeSub?.date_of_end).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}</b>`;

  return (
    <div className="min-w-0">
      <BillingHeader />

      {isEmpty ? (
        <BillingEmptyState className="mt-5" onStart={handleNavigate} />
      ) : (
        <>
          <PlanSummaryCard
            className="mt-5"
            planName={plan.planName}
            statusLabel={plan.statusLabel}
            cadence={plan.cadence}
            cadenceTone={plan.cadenceTone}
            card={plan.card}
            currency={plan.currency}
            amount={plan.amount}
            period={plan.period}
            meta={
              plan.meta?.renew ? (
                <>
                  {plan.meta.label}{" "}
                  <button
                    type="button"
                    onClick={handleManageSubscription}
                    className="underline font-semibold text-[var(--plan-warn)]"
                  >
                    Click here to renew today
                  </button>
                  .
                </>
              ) : plan.meta ? (
                <>
                  {plan.meta.label}{" "}
                  <b className="font-semibold text-white">{plan.meta.value}</b>
                </>
              ) : null
            }
            action={
              plan.action && {
                label: plan.action.label,
                disabled: plan.action.disabled,
                onClick:
                  plan.action.kind === "portal" ? handleManageSubscription : handleNavigate,
              }
            }
          >
            {plan.credits && (
              <p className="m-0">
                {plan.credits.label}{" "}
                <b className="font-semibold text-white">{plan.credits.value}</b>
              </p>
            )}
          </PlanSummaryCard>

          {/* Transcribed: hidePlan takes the whole history away, not just the
              plan's button. */}
          {!hidePlan && (
            <BillingHistoryCard
              className="mt-[22px]"
              rows={rows}
              onDownload={handleDownload}
              onDownloadAll={handleDownloadAll}
            />
          )}

          {!hidePlan && showCancel && (
            <div className="mt-5">
              <button
                type="button"
                disabled={cancelBlocked}
                onClick={() => (cancelBlocked ? null : setGetConfirm(true))}
                className="text-[13.5px] font-semibold text-[var(--coral)] font-sans disabled:cursor-not-allowed disabled:text-[var(--muted)]"
              >
                {cancelLabel}
              </button>
            </div>
          )}
        </>
      )}

      {/* Both modals are reused exactly as the live screen uses them — same
          components, same copy, same handlers. Restyling them is not part of
          this ticket. */}
      {getConfirm && (
        <Modal modalHandle={() => setGetConfirm(false)}>
          <CancelSubscriptionConfirmation
            onclick={() => setGetConfirm(false)}
            handleCancelSubscription={handleCancelSubscription}
            title={" Are you sure?"}
            des2={endsOn("Your current subscription will end on")}
            des={
              "Please confirm your decision to cancel your subscription. Remember, upon cancellation, you'll lose claim to all premium features."
            }
            leftButton={"Keep Subscription"}
            rightButton={"Confirm Cancellation"}
          />
        </Modal>
      )}

      {cancelSuccess && (
        <Modal modalHandle={() => setCancelSuccess(false)}>
          <CancelationSuccess
            title={"Subscription Cancelled?"}
            des2={endsOn("Your current subscription will end on")}
            des={
              "We're sad to see you go. Your subscription has been cancelled. You'll have claim to premium features until the end of your billing cycle."
            }
          />
        </Modal>
      )}
    </div>
  );
};

export default BillingPanel;
